import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, LinearProgress, Box
} from '@mui/material';
import { Timer as TimerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../hooks/redux.hooks';
import { logout } from '../store/slices/authSlice';
import axiosInstance from '../services/axiosInstance';
import { recordActivity, getIdleSeconds } from '../services/activityTracker';
import { useLanguage } from '../contexts/LanguageContext';

const IDLE_THRESHOLD_SECONDS = 60 * 60; // 60 min idle → show warning
const WARNING_DURATION_SECONDS = 60;     // 60s countdown inside warning
const CHECK_INTERVAL_MS = 10 * 1000;     // poll idle status every 10s
const THROTTLE_MS = 1000;                // dedupe high-frequency events

// Discrete user-input events (always recorded)
const DISCRETE_EVENTS: ReadonlyArray<keyof WindowEventMap> = [
  'mousedown', 'click', 'keydown', 'touchstart', 'wheel'
];
// High-frequency events (throttled to once per second)
const THROTTLED_EVENTS: ReadonlyArray<keyof WindowEventMap> = [
  'mousemove', 'scroll'
];

const IdleTimeoutManager: React.FC = () => {
  const isAuthenticated = useAppSelector(s => s.auth.isAuthenticated);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isRTL = language === 'ar';

  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(WARNING_DURATION_SECONDS);

  const throttleRef = useRef(0);
  const countdownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isLoggingOutRef = useRef(false);

  // ── Activity listeners (real user input only) ──────────────────────────
  useEffect(() => {
    if (!isAuthenticated) return;

    const discreteHandler = () => recordActivity();

    const throttledHandler = () => {
      const now = Date.now();
      if (now - throttleRef.current >= THROTTLE_MS) {
        throttleRef.current = now;
        recordActivity();
      }
    };

    DISCRETE_EVENTS.forEach(ev => window.addEventListener(ev, discreteHandler, { passive: true }));
    THROTTLED_EVENTS.forEach(ev => window.addEventListener(ev, throttledHandler, { passive: true }));

    // Ensure activity is fresh on mount (just-logged-in user is "active now")
    recordActivity();

    return () => {
      DISCRETE_EVENTS.forEach(ev => window.removeEventListener(ev, discreteHandler));
      THROTTLED_EVENTS.forEach(ev => window.removeEventListener(ev, throttledHandler));
    };
  }, [isAuthenticated]);

  // ── Periodic idle check (only when warning is NOT already shown) ──────
  useEffect(() => {
    if (!isAuthenticated || showWarning) return;

    const intervalId = setInterval(() => {
      if (getIdleSeconds() >= IDLE_THRESHOLD_SECONDS) {
        setCountdown(WARNING_DURATION_SECONDS);
        setShowWarning(true);
      }
    }, CHECK_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [isAuthenticated, showWarning]);

  // ── Force logout (stable reference) ────────────────────────────────────
  const performLogout = useCallback(async () => {
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;
    setShowWarning(false);
    try {
      await axiosInstance.post('/Auth/logout').catch(() => {});
    } finally {
      dispatch(logout());
      navigate('/login', { replace: true });
      // Reset so a new session after re-login can trigger again
      setTimeout(() => { isLoggingOutRef.current = false; }, 1000);
    }
  }, [dispatch, navigate]);

  // ── Countdown timer while warning is shown ─────────────────────────────
  useEffect(() => {
    if (!showWarning) {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
      return;
    }

    countdownTimerRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          // Defer logout so React can finish this tick cleanly
          setTimeout(() => performLogout(), 0);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
    };
  }, [showWarning, performLogout]);

  const stayLoggedIn = () => {
    recordActivity();
    setShowWarning(false);
  };

  if (!isAuthenticated) return null;

  return (
    <Dialog
      open={showWarning}
      dir={isRTL ? 'rtl' : 'ltr'}
      disableEscapeKeyDown
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, border: '2px solid #e53935' } }}
    >
      <DialogTitle sx={{
        background: 'linear-gradient(135deg, #e53935 0%, #ef5350 100%)',
        color: '#fff',
        textAlign: 'center',
        fontWeight: 'bold',
        py: 2
      }}>
        <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
          <TimerIcon />
          {isRTL ? 'تنبيه: عدم نشاط' : 'Inactivity Warning'}
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 3, pb: 1, textAlign: 'center' }}>
        <Typography variant="body1" mb={2}>
          {isRTL
            ? 'لم نسجّل أي نشاط على حسابك منذ ساعة. سيتم تسجيل الخروج تلقائياً خلال:'
            : 'No activity detected for an hour. Auto logout in:'}
        </Typography>
        <Typography variant="h2" sx={{
          fontWeight: 'bold', color: '#e53935', my: 2,
          fontFamily: 'monospace', fontSize: '4rem'
        }}>
          {countdown}s
        </Typography>
        <LinearProgress
          variant="determinate"
          value={(countdown / WARNING_DURATION_SECONDS) * 100}
          sx={{
            height: 10, borderRadius: 1,
            bgcolor: 'rgba(229, 57, 53, 0.15)',
            '& .MuiLinearProgress-bar': { bgcolor: '#e53935' }
          }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          {isRTL
            ? 'اضغط الزر بالأسفل إذا كنت لا تزال تعمل على الجهاز'
            : 'Click the button below if you are still working'}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2, justifyContent: 'center' }}>
        <Button
          variant="contained"
          size="large"
          onClick={stayLoggedIn}
          sx={{
            minWidth: 220, py: 1.5, fontWeight: 'bold', fontSize: '1rem',
            background: 'linear-gradient(135deg, #43a047 0%, #66bb6a 100%)',
            '&:hover': { background: 'linear-gradient(135deg, #2e7d32 0%, #43a047 100%)' }
          }}
        >
          {isRTL ? '✓ متابعة العمل' : '✓ Stay Logged In'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IdleTimeoutManager;
